import * as snowplowTracker from '@snowplow/browser-tracker'
import { DebuggerPlugin } from '@snowplow/browser-plugin-debugger'
import env from '@shared/env'
import { toObjectOfNonNullishEntries } from '@shared/utils'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { watch, watchEffect } from 'vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import {
  getSignupConsents,
  shouldEnableTracker,
  shouldEnableEuSessionReplay,
  isInDiga,
} from '@shared/tracking/consentPolicy.js'
import { redactSensitiveUrlsDeep } from '@shared/tracking/urlRedaction.js'
import { routeNameOrStartupPage } from '@shared/actionLinks/pages.js'
import oneTimeTokenPlugin from '@shared/tracking/oneTimeTokenPlugin.js'

const TRACKING_OPT_OUT_COOKIE_NAME = 'opt-out'
let trackingEnabled = false
let kaiaUserContext = null
let kaiaCorporate = null
let sharedStoreContext = null
let featureFlagStoreContext = null

let matomoScriptLoaded = false
let matomoActive = false

// Any query param whose key contains one of these substrings is stripped before sending to Matomo.
const MATOMO_EXCLUDED_PARAM_SUBSTRINGS = [
  'token',
  'code',
  'email',
  'lookupKey',
  'deeplink',
]

function enableMatomo() {
  window._paq = window._paq || []
  // Clear any opt-out cookie persisted from a prior session.
  window._paq.push(['forgetUserOptOut'])
  if (!matomoScriptLoaded) {
    window._paq.push(['setCookieDomain', '*.startkaia.de'])
    window._paq.push(['setDomains', ['*.startkaia.de']])
    window._paq.push(['enableCrossDomainLinking'])
    window._paq.push(['setTrackerUrl', env.VITE_MATOMO_URL + 'matomo.php'])
    window._paq.push(['setSiteId', '1'])
    window._paq.push(['enableLinkTracking'])
    const g = document.createElement('script')
    const s = document.getElementsByTagName('script')[0]
    g.async = true
    g.src = `https://cdn.matomo.cloud/${new URL(env.VITE_MATOMO_URL).hostname}/matomo.js`
    s.parentNode.insertBefore(g, s)
    matomoScriptLoaded = true
  }
  matomoActive = true
}

function disableMatomo() {
  window._paq = window._paq || []
  window._paq.push(['optUserOut'])
  matomoActive = false
}

const getCookieString = () =>
  typeof document === 'undefined' ? '' : document.cookie

const getRouteName = (router) =>
  routeNameOrStartupPage(router.currentRoute.value?.name)

const Tracker = {
  initSnowplow({
    persistedConfigStore,
    corporateStore,
    userStore,
    sharedStore,
    featureFlagStore,
    router,
  }) {
    sharedStoreContext = sharedStore
    featureFlagStoreContext = featureFlagStore

    snowplowTracker.newTracker(
      'web-checkout-tracker',
      env.VITE_SNOWPLOW_COLLECTOR,
      {
        appId: env.VITE_SNOWPLOW_APP_ID,
        platform: 'web',
        eventMethod: 'post',
        cookieSecure: true,
        postPath: '/wc/t',
        plugins: [oneTimeTokenPlugin],
      },
    )

    snowplowTracker.setOptOutCookie(TRACKING_OPT_OUT_COOKIE_NAME)

    // setup snowplow debugger on all but production builds
    // this allows to see what exactly gets tracked in the browsers js console
    if (!env.isProd) {
      snowplowTracker.addPlugin({ plugin: DebuggerPlugin() })
    }

    watchEffect(
      () => {
        const user = userStore.user
        const isTrackingAllowed = shouldEnableTracker({
          env,
          isFromApp: isFromApp(),
          performanceTrackingEnabled:
            persistedConfigStore.performanceTrackingEnabled,
          user,
          routeName: getRouteName(router),
          initialQuery: sharedStore.initialQuery,
          cookies: getCookieString(),
          digaActivated: sharedStore.digaActivated,
        })

        if (isTrackingAllowed) {
          this.enableTracking()
        } else {
          this.disableTracking()
        }

        this.setUser(env.isUs || isTrackingAllowed ? user : null)
      },
      // flush:'sync' ensures trackingEnabled is set before router.afterEach fires screen-view events.
      { flush: 'sync' },
    )

    watchEffect(() => {
      this.setCorporate(corporateStore.corporate)
    })

    if (env.isEu) {
      watchEffect(() => {
        const shouldRecord = shouldEnableEuSessionReplay({
          user: userStore.user,
          routeName: getRouteName(router),
          initialQuery: sharedStore.initialQuery,
          cookies: getCookieString(),
          digaActivated: sharedStore.digaActivated,
        })

        if (shouldRecord) {
          datadogRum.startSessionReplayRecording()
        } else {
          datadogRum.stopSessionReplayRecording()
        }
      })
    }
  },
  initMatomo({ sharedStore, userStore, router }) {
    // VITE_MATOMO_URL is only set in EU production to prevent staging traffic in prod analytics.
    if (!env.isEu || !env.VITE_MATOMO_URL) return

    // watchEffect must be registered first so it runs synchronously and queues the Matomo
    // config before the route watcher below fires its first trackPageView.
    watchEffect(() => {
      const routeName = getRouteName(router)
      const inDiga = isInDiga({
        routeName,
        digaActivated: sharedStore.digaActivated,
        user: userStore.user,
      })

      // Matomo is cookieless/consent-free outside DiGA; pause it inside DiGA where stricter consent rules apply.
      if (!inDiga && !matomoActive) {
        enableMatomo()
      } else if (inDiga && matomoActive) {
        disableMatomo()
      }
    })

    watch(router.currentRoute, (route) => {
      if (!matomoActive) return

      const query = Object.fromEntries(
        Object.entries(route.query).filter(
          ([key]) =>
            !MATOMO_EXCLUDED_PARAM_SUBSTRINGS.some((s) => key.includes(s)),
        ),
      )
      // router.resolve() handles base path, query serialization, and array values.
      const { href } = router.resolve({ path: route.path, query })
      window._paq.push(['setCustomUrl', href])
      window._paq.push(['trackPageView'])
    })
  },

  setUser(user) {
    kaiaUserContext =
      user == null
        ? null
        : {
            schema: 'iglu:com.kaiahealth/kaia_user/jsonschema/2-3-0',
            data: {
              id: user.id,
              guid: user.guid ?? null,
              profile_language: user.profile?.language ?? null,
              profile_corporate_id: user.profile?.corporateId ?? null,
            },
          }
    snowplowTracker.setUserId(user?.id?.toString() || null)

    if (user?.guid != null) {
      datadogRum.setUser({ id: user?.guid })
      datadogLogs.setUser({ id: user?.guid })
    } else {
      datadogRum.clearUser()
      datadogLogs.clearUser()
    }
  },
  getSignupConsents() {
    return getSignupConsents({
      initialQuery: sharedStoreContext?.initialQuery,
      cookies: getCookieString(),
    })
  },

  getFeatureFlagPayload() {
    if (featureFlagStoreContext == null) return {}
    const payload = {}
    const { flagsByKey, flagKeys, flags } = featureFlagStoreContext
    flagKeys.forEach((key) => {
      payload[`feature_flag_${key}`] = flags[key]
      const { remoteExperimentKey, remoteVariantKey } = flagsByKey[key]
      if (remoteExperimentKey != null && remoteVariantKey != null) {
        payload[`experiment_${remoteExperimentKey}`] = remoteVariantKey
      }
    })
    return payload
  },

  setCorporate(corporate) {
    kaiaCorporate = corporate ?? null
  },

  trackViewScreen(screenName, customPayload) {
    this.trackKaiaEvent(
      {
        event_name: 'c.lfcycl.view_screen',
        action: 'view',
        object_type: 'screen',
        screen_name: screenName,
      },
      customPayload,
    )
  },

  trackButtonSelect(label, screenName, customPayload) {
    this.trackKaiaEvent(
      {
        event_name: 'c.lfcycl.select_button',
        action: 'view',
        object_type: 'screen',
        screen_name: screenName,
      },
      {
        label,
        ...customPayload,
      },
    )
  },

  /**
   * @param payload {Object}
   * @param payload.event_name {string} eg 'c.lfcycl.submit_initial_assessment'
   * @param payload.action {string} eg 'submit'
   * @param payload.object_type {string} eg 'initial_assessment'
   * @param payload.screen_name {string} eg 'onb_finish_congratulations'
   * @param [customPayload] {Object} key-value additional data
   */
  trackKaiaEvent(payload, customPayload) {
    if (!trackingEnabled) return

    if (typeof payload?.event_name !== 'string' || !payload.event_name) {
      throw new Error(
        'VueSnowplow#trackKaiaEvent:' +
          ' expected payload.event_name to be non-empty string,' +
          ` ${payload?.event_name} got instead`,
      )
    }

    const context = []

    if (kaiaUserContext != null) {
      context.push(kaiaUserContext)
    }

    context.push({
      schema: 'iglu:com.kaiahealth/custom_payload/jsonschema/1-0-0',
      data: toObjectOfNonNullishEntries({
        from_app: isFromApp(),
        ...(kaiaCorporate != null && {
          corporate_id: kaiaCorporate.id,
          corporate_name: kaiaCorporate.title,
          corporate_type: kaiaCorporate.corporateTypes?.toString() || '',
          corporate_channel: kaiaCorporate.corporateChannel || null,
          ...(env.isEu && {
            public_flow: kaiaCorporate?.isPublicInsurance,
            private_flow: kaiaCorporate?.isPrivateInsurance,
          }),
        }),
        ...this.getFeatureFlagPayload(),
        ...customPayload,
      }),
    })

    snowplowTracker.trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.kaiahealth/kaia_event/jsonschema/1-0-0',
        data: {
          app_area: 'lifecycle',
          source: 'web-checkout',
          ...payload,
        },
      },
      context,
    })
  },

  trackPageView(url, additionalData) {
    if (!trackingEnabled) return
    snowplowTracker.setCustomUrl(url)
    const context = []

    if (additionalData) {
      context.push({
        schema: 'iglu:com.kaiahealth/custom_payload/jsonschema/1-0-0',
        data: toObjectOfNonNullishEntries(additionalData),
      })
    }
    snowplowTracker.trackPageView({ context })
  },

  enableTracking() {
    if (trackingEnabled) {
      return
    }

    console.debug('Tracking enabled')
    trackingEnabled = true
    // delete the opt-out cookie
    document.cookie =
      TRACKING_OPT_OUT_COOKIE_NAME +
      '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
  },

  disableTracking() {
    if (trackingEnabled) {
      // to be safe we disable always, but only log it once
      console.debug('Tracking disabled')
    }
    // sadly there is no clear way to disable the tracker altogether
    trackingEnabled = false
    kaiaUserContext = null
    kaiaCorporate = null
    snowplowTracker.clearUserData()
    snowplowTracker.clearGlobalContexts()
    document.cookie = 'sp=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie = TRACKING_OPT_OUT_COOKIE_NAME + '=; Path=/;'
  },

  /**
   * Initialize DataDog for RUM (incl crash reporting) and log collection.
   * Proxy can be configured via environment variables to enable EU-compliant solution.
   */
  initDatadog() {
    const clientToken = env.VITE_DATADOG_CLIENT_TOKEN
    const applicationId = env.VITE_DATADOG_APP_ID
    if (applicationId == null || clientToken == null) return
    // Comment this out if you want DD to be enabled on localhost
    if (window.location.hostname === 'localhost') return

    const commonConfig = {
      clientToken: clientToken,
      site: 'datadoghq.com',
      service: `${env.VITE_DISEASE}-${env.VITE_GEO}-webcheckout`,
      env: env.VITE_MODE,
      version: env.VITE_VERSION,
      proxy: env.VITE_DATADOG_PROXY,
    }
    datadogRum.init({
      ...commonConfig,
      applicationId: applicationId,
      allowedTracingUrls: [
        // trace all kaia domains and subdomains but not the snowplow collector
        /https:\/\/(?!collector\.)(?:.*\.)?kaiahealth\.(de|com)/,
      ],
      trackUserInteractions: true,
      trackResources: !env.isProd,
      trackLongTasks: !env.isProd,
      telemetrySampleRate: 0,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: env.isEu,
      beforeSend: (event) => {
        if (
          event.error?.resource?.url?.includes('snowplow') ||
          event.resource?.url?.includes('snowplow')
        )
          return false
        redactSensitiveUrlsDeep(event)
        return true
      },
    })
    datadogLogs.init({
      ...commonConfig,
      telemetrySampleRate: 0,
      forwardConsoleLogs: ['log', 'info', 'warn', 'error'],
      forwardReports: ['intervention', 'deprecation'], // filter out csp_violations
      forwardErrorsToLogs: true,
      beforeSend: (log) => {
        redactSensitiveUrlsDeep(log)
        return true
      },
    })
  },
}

export default Tracker
